/**
 * A-GTCC-VIEW-PSET-DZ1 — GTCC view "Danger Zone" (dz), 1:1 after
 * `docs/design/GTCC_REFERENCE_2026-09-01.html`, `div[data-v="dz"]`
 * (lines 886-899).
 *
 * Layout of the reference, element for element:
 *   - head "Danger Zone" in the crit colour with the freshness badge and the
 *     `.sub` "Unumkehrbare Aktionen — doppelt bestätigt, voll auditiert, nie
 *     aus einem Alarm heraus.",
 *   - panel "Purge Center — unumkehrbare Aktionen" with the crit border and the
 *     three crit rows plus the muted audit-tombstone footnote,
 *   - panel "Letzte Purge-Jobs" from `GET /v1/admin/tenants/purge-jobs`.
 *
 * # The hard boundary of this package
 *
 * The deletion paths are protected. This file contains no delete, no purge and
 * no revoke: there is not a single mutation in it, and the one request it makes
 * is the read of the job history. The three rows of the Purge Center do not
 * re-implement anything — they lead to the flows that already exist, with their
 * preview, their typed confirmation, their countdown and their audit untouched:
 *
 *   - "Tenant endgültig löschen" reveals the EXISTING `PurgeCenterContent`
 *     (`purge/PurgeCenterPage.tsx`) in place, the very component the previous
 *     `dz` view mounted. It has to stay reachable from this key: `dz` is its
 *     only entry point in the deck, so navigating away instead of disclosing it
 *     would have orphaned the flow. The row itself executes nothing; it opens
 *     the flow, which then asks for everything it asked for before.
 *   - "Alle Sitzungen eines Nutzers beenden" jumps to the view that owns the
 *     revoke today (`views/sec/`, `useRevokeGtccSession.ts`).
 *   - "Gerätedaten-Purge (DSGVO-Auskunft)" has NO flow in this console. The
 *     reference's own note says why — it runs "nur mit Fall-Nummer", i.e.
 *     outside the cockpit. So this row is not an activator: it is a row that
 *     names the action and says where it happens. Wiring it to any other target
 *     would have been a guess, and a guess on a deletion path is the one thing
 *     this package must not produce.
 *
 * The role gate of the predecessor is kept verbatim in effect: a session that
 * is visibly outside the platform-owner boundary sees the refusal and fires no
 * purge read at all.
 *
 * Honesty (Owner P8/P9/P10): the badge never dates the page from the browser
 * clock, and an unknown job state reads "nicht messbar" rather than being
 * sorted into a colour it did not earn (`api/dz.ts:dzPurgeJobVerdict`).
 */
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Stack,
  StatePill,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState, type CSSProperties, type ReactNode } from "react";

import { usePlatformOwner } from "../../../access/platform-owner.js";
import { PurgeCenterContent } from "../../../purge/PurgeCenterPage.js";
import { useSession } from "../../../session/SessionProvider.js";
import {
  DZ_PURGE_JOBS_KEY,
  dzMaskActor,
  dzPurgeJobVerdict,
  fetchDzPurgeJobs,
  type DzPurgeJobDto,
  type DzPurgeJobsDto,
} from "../../api/dz.js";
import { writeGtccViewKeyToLocation } from "../../deep-link-url.js"; // Blatt-Modul: bricht den Zyklus registry -> View -> deep-link -> registry.
import { FreshBadge } from "../../ui/primitives.js";
import {
  ControlActionRow,
  ControlMono,
  ControlNote,
  ControlNotMeasurable,
  ControlPanel,
  ControlRow,
  toneColor,
  type ControlTone,
} from "../control/ControlPrimitives.js";

/* --------------------------------------------------------------- styles */

/** The reference paints the head of this view — and only this one — crit. */
const HEAD_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3)",
  flexWrap: "wrap",
};

/*
 * C-GTCC-SHELL-1ZU1 — Seitentitel = `h1` der Referenz
 * (GTCC_REFERENCE_2026-09-01.html:67): Sora 19 px/700 in `--tx`. Die Werte
 * kommen aus dem Token-Satz, damit jede Deck-View denselben Titel traegt;
 * vorher hat jede View ihre eigene Typo-Stufe gesetzt (15/19/20/24 px).
 */
const TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: "var(--gt-cmp-gtcc-view-title-family)",
  fontSize: "var(--gt-cmp-gtcc-view-title-size)",
  fontWeight: "var(--gt-cmp-gtcc-view-title-weight)",
  color: toneColor("crit"),
};

const SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/** `border-color: color-mix(in srgb, var(--crit) 40%, transparent)` of line 888. */
const DANGER_PANEL_STYLE: CSSProperties = {
  border: `1px solid color-mix(in srgb, ${toneColor("crit")} 40%, transparent)`,
  borderRadius: "var(--gt-sys-radius-md)",
  padding: "var(--gt-sys-space-4)",
};

const DANGER_HEADING_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-md)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  color: toneColor("crit"),
};

/** Never refetch faster than 30 s, like every other GTCC view. */
const DZ_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

/* ------------------------------------------------------------- job rows */

const VERDICT_TONE: Record<ReturnType<typeof dzPurgeJobVerdict>, ControlTone> = {
  ok: "ok",
  warn: "warn",
  crit: "crit",
  info: "info",
  unmeasurable: "muted",
};

/**
 * Zustaende, die nur angesehen und nicht ausgefuehrt wurden.
 *
 * Die Referenz nennt den Ausloeser an dem Job, der etwas getan hat
 * („succeeded · info@geletec.de", Zeile 896), und an dem bloss vorgeschauten
 * keinen (Zeile 897) — der Befund „zweite Zeile zeigt zusaetzlich eine
 * Adresse" der Abnahme E-GTCC-ACCEPT2. Es wird nichts erfunden: die Zeile
 * laesst eine vorhandene Angabe weg, sie behauptet keine fehlende.
 */
const DZ_PREVIEW_ONLY_STATES: ReadonlySet<string> = new Set(["pending", "previewed"]);

function JobRow(props: { job: DzPurgeJobDto }): ReactNode {
  const { t } = useLocale();
  const { job } = props;
  const state = typeof job.state === "string" ? job.state : "";
  const actorEmail = typeof job.actorEmail === "string" ? job.actorEmail : "";
  const tenantDisplayLabel =
    typeof job.tenantDisplayLabel === "string" ? job.tenantDisplayLabel : "—";
  const verdict = dzPurgeJobVerdict(state);
  const tone = VERDICT_TONE[verdict];
  const actor = DZ_PREVIEW_ONLY_STATES.has(state) ? null : dzMaskActor(actorEmail);
  return (
    <div data-testid={`gtcc-dz-job-${job.id}`} data-verdict={verdict}>
      <ControlRow
        tone={tone}
        label={tenantDisplayLabel}
        value={
          verdict === "unmeasurable" ? (
            <ControlNotMeasurable label={t("admin.gtcc.dz.jobs.unknown_state")} />
          ) : (
            <ControlMono tone={tone}>
              {/* Zustand und Ausloeser sind Serverwerte; der Punkt dazwischen
                  ist die Schreibweise der Referenz, kein uebersetzter Text. */}
              {actor === null ? state : `${state} · ${actor}`}
            </ControlMono>
          )
        }
      />
    </div>
  );
}

/**
 * „Letzte Purge-Jobs" als Zeilen, nicht als Tabelle.
 *
 * Die Referenz (Zeilen 895-898) zeichnet die Historie als `.row`-Zeilen mit
 * Punkt, Tenant und nachlaufendem `.mono`-Wert — ohne Spaltenkoepfe. Der
 * Befund der Abnahme E-GTCC-ACCEPT2 lautet genau so. Reine Darstellung: die
 * gelesene Nutzlast, der Loeschpfad und alle Schreibwege bleiben unberuehrt.
 */
function JobsList(props: { data: DzPurgeJobsDto }): ReactNode {
  const { t } = useLocale();
  const items = Array.isArray(props.data.items) ? props.data.items : [];

  if (items.length === 0) {
    return (
      <div data-testid="gtcc-dz-jobs-empty">
        <ControlNote>{t("admin.gtcc.dz.jobs.empty")}</ControlNote>
      </div>
    );
  }

  return (
    <div data-testid="gtcc-dz-jobs" aria-label={t("admin.gtcc.dz.jobs.caption")} role="group">
      {items.map((job) => (
        <JobRow key={job.id} job={job} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ view */

export function DzView(): ReactNode {
  const { t } = useLocale();
  const session = useSession();
  const owner = usePlatformOwner(session);
  const [purgeOpen, setPurgeOpen] = useState(false);

  /* The read is skipped outside the platform-owner boundary — the predecessor
     did the same, and a purge read fired for a session that may not see it is
     a request nobody asked for. */
  const jobsQuery = useQuery({
    queryKey: DZ_PURGE_JOBS_KEY,
    queryFn: ({ signal }) => fetchDzPurgeJobs(signal),
    enabled: owner,
    ...DZ_QUERY_OPTIONS,
  });

  const goTo = useCallback((key: string) => {
    writeGtccViewKeyToLocation(key, false, import.meta.env.BASE_URL);
    if (typeof window !== "undefined") window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  if (!owner) {
    return (
      <Card elevation={1}>
        <div data-testid="gtcc-dz-role-gate">
          <EmptyState
            title={t("admin.gtcc.dz.forbidden.title")}
            body={t("admin.gtcc.dz.forbidden.body")}
          />
        </div>
      </Card>
    );
  }

  /*
   * `/v1/admin/tenants/purge-jobs` carries no measurement timestamp and this
   * view computes none: the badge therefore says the age is not measurable
   * instead of printing one from the browser clock (P8). It is marked stale so
   * it is never read as a fresh page.
   */
  const fresh = (
    <span data-testid="gtcc-dz-fresh" data-stale="true">
      <FreshBadge measuredAt={null} stale />
    </span>
  );

  const result = jobsQuery.data;
  const jobsBody: ReactNode =
    result !== undefined && result.ok ? (
      <JobsList data={result.data} />
    ) : result === undefined && !jobsQuery.isError ? (
      <div data-testid="gtcc-dz-jobs-loading">
        <LoadingState label={t("admin.gtcc.dz.loading")} />
      </div>
    ) : (
      <div data-testid="gtcc-dz-jobs-failure">
        <ErrorState
          severity="warn"
          title={t("admin.gtcc.dz.failure.title")}
          body={
            result !== undefined && !result.ok && result.kind === "disabled"
              ? t("admin.gtcc.dz.failure.disabled")
              : result !== undefined && !result.ok && result.kind === "forbidden"
                ? t("admin.gtcc.dz.failure.forbidden")
                : result !== undefined && !result.ok && result.kind === "upstream"
                  ? t("admin.gtcc.dz.failure.upstream", { status: result.httpStatus })
                  : t("admin.gtcc.dz.failure.transport")
          }
        />
      </div>
    );

  return (
    <Stack gap={3} data-testid="gtcc-view-dz">
      <div data-testid="gtcc-dz-head">
        <div style={HEAD_STYLE}>
          {/* h2, nicht h1: die Seitenueberschrift gehoert dem Shell. */}
          <h2 style={TITLE_STYLE} data-testid="gtcc-dz-title">
            {t("admin.gtcc.dz.head.title")}
          </h2>
          <StatePill tone="danger" label={t("admin.gtcc.dz.danger_label")} />
          {fresh}
        </div>
        <p style={SUB_STYLE} data-testid="gtcc-dz-sub">
          {t("admin.gtcc.dz.head.subtitle")}
        </p>
      </div>

      <Card elevation={0} data-testid="gtcc-dz-purge-center-panel">
        <div style={DANGER_PANEL_STYLE}>
          <Stack gap={2}>
            <h3 style={DANGER_HEADING_STYLE}>{t("admin.gtcc.dz.purge.title")}</h3>

            <ControlActionRow
              testId="gtcc-dz-purge-tenant"
              tone="crit"
              label={t("admin.gtcc.dz.purge.tenant")}
              value={<ControlMono tone="crit">{t("admin.gtcc.dz.purge.tenant_hint")}</ControlMono>}
              onActivate={() => setPurgeOpen(true)}
            />
            {/* Kein Ablauf in der Konsole: die Zeile benennt die Aktion und
                sagt, wo sie stattfindet — sie aktiviert nichts. */}
            <ControlRow
              testId="gtcc-dz-purge-devices"
              tone="crit"
              label={t("admin.gtcc.dz.purge.devices")}
              value={<ControlMono>{t("admin.gtcc.dz.purge.devices_hint")}</ControlMono>}
            />
            <ControlActionRow
              testId="gtcc-dz-purge-sessions"
              tone="crit"
              label={t("admin.gtcc.dz.purge.sessions")}
              value={
                <ControlMono tone="crit">{t("admin.gtcc.dz.purge.sessions_hint")}</ControlMono>
              }
              onActivate={() => goTo("sec")}
            />

            <ControlNote testId="gtcc-dz-purge-note">{t("admin.gtcc.dz.purge.note")}</ControlNote>

            {purgeOpen ? (
              <div data-testid="gtcc-dz-purge-center-open">
                <PurgeCenterContent
                  testId="gtcc-dz-purge-center"
                  titleKey="admin.gtcc.dz.title"
                  subtitleKey="admin.gtcc.dz.subtitle"
                  headingLevel="h2"
                />
              </div>
            ) : null}
          </Stack>
        </div>
      </Card>

      <ControlPanel testId="gtcc-dz-jobs-panel" title={t("admin.gtcc.dz.jobs.title")}>
        {jobsBody}
      </ControlPanel>

      <p
        data-testid="gtcc-dz-scope-note"
        style={{
          margin: 0,
          color: "var(--gt-sys-text-muted)",
          fontSize: "var(--gt-sys-font-size-xs)",
        }}
      >
        {t("admin.gtcc.dz.scope_note")}
      </p>
    </Stack>
  );
}
